import { useEffect, useState } from 'react';
import AddressForm from '../AddressForm';
import { Cookies } from 'react-cookie';
import Card from '../Card';
import Input from '../Input';
import AutoSelect from '../AutoSelect';
import Button from '../Button';
import { fetchCompetitionTeam } from '../../api/api';
import { useMemberOptions } from '../../hooks/useMemberOptions';
import { useStep } from '../../context/StepContext';
import Header from '../Header';
import FlashMessage from '../FlashMessage';
import { isZipValid } from '../../utils/addressValidation';
import { normalizeStateToCode } from '../../utils/usStates';

function TeamBuilder({
    competition,
    competition_event,
    divisionSelection,
    editingTeam,
    editingTeamId,
    onSubmit
  }) {
  const { officeOptions, allRepOptions } = useMemberOptions()
  const cookies = new Cookies();
  const [owner, setOwner] = useState(null)
  const [error, setError] = useState('')
  const [errorType, setErrorType] = useState(null);
  const [office, setOffice] = useState(null);
  const [teamName, setTeamName] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [address, setAddress] = useState({
    street: '',
    city: '',
    state: '',
    zip: '',
  });

  const handleChange = (event) => {
    const { name, value } = event.target;
    setAddress((prevAddress) => ({
      ...prevAddress,
      [name]: value,
    }));
  };

  useEffect(() => {
    !editingTeam && setTeamName('')
  }, [editingTeam])

  useEffect(() => {
    const role = cookies.get('role');
    const badgeId = Number(cookies.get('userBadgeId'));

    role === 'user' && findOwner(badgeId)
  }, [allRepOptions]);

  const findOwner = (creatorId) => {
    if (allRepOptions.length > 0) {
      const found = allRepOptions.find(rep => Number(rep.employee_id) === creatorId);
      setOwner(found);
    }
  }

  const findOffice = (officeName) => {
    if (officeOptions.length > 0 && officeName) {
      const found = officeOptions.find(opt => opt.label === officeName);
      found && setOffice(found);
    }
  }

  const handleCreator = (rep) => {
    setOwner(rep)
  }

  const handleSubmit = async () => {
    setError('');
    setErrorType(null);

    if (!competition || !competition_event) {
      setError('Competition or event is not selected.');
      setErrorType('failure')
      return;
    }

    if (!owner.employee_id) {
      setError('Team owner is not set.');
      setErrorType('failure');
      return;
    }

    // Only enforced on brand-new teams: editing a pre-existing team may carry
    // an address saved before this validation existed, and shouldn't be
    // blocked from saving just because that legacy value doesn't conform.
    if (competition.include_address && !editingTeam) {
      if (!address.state) {
        setError('Please select a state for the team address.');
        setErrorType('failure');
        return;
      }
      if (!isZipValid(address.zip)) {
        setError('Please enter a valid zip code (e.g. 84003).');
        setErrorType('failure');
        return;
      }
    }

    setIsSubmitting(true);
    try {
      const teamPayload = {
        name: teamName,
        division_selection: divisionSelection,
        address,
        competition_event_id: competition_event.id,
        competition_id: competition.id,
        creator_employee_id: owner.employee_id,
        office_name: office?.label,
        office_restriction: competition.office_restriction,
        division: divisionSelection,
        editingTeamId,
        isEditing: editingTeam,
        last_modified_by: cookies.get('role')
      };

      await onSubmit(teamPayload)
    } catch (err) {
      setError(err.message);
      setErrorType('failure')
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    if (editingTeam) {
      fetchCompetitionTeam(editingTeamId)
        .then(res => {
          setTeamName(res.name);
          findOffice(res.office_name);

          let addressObj = { street: '', city: '', state: '', zip: '' };

          if (typeof res.address === 'string') {
            const parts = res.address.split(',').map(p => p.trim());
            const [street = '', city = '', stateZip = ''] = parts;

            // stateZip is "utah 84003"
            const stateZipParts = stateZip.trim().split(/\s+/);
            const zip = stateZipParts.pop() || '';
            const rawState = stateZipParts.join(' ');
            // Legacy addresses may have a full state name or odd casing;
            // normalize where possible so the dropdown reflects it accurately
            // instead of silently defaulting to its first option.
            const state = normalizeStateToCode(rawState) || rawState;

            addressObj = { street, city, state, zip };
          }

          setAddress(addressObj);
          findOwner(res.creator_employee_id);

          if (!res.office_name) {
            const ownerRep = allRepOptions.find(rep => Number(rep.employee_id) === res.creator_employee_id);
            ownerRep?.office && findOffice(ownerRep.office);
          }
        })
        .catch(error => {
          setError(error.message);
          setErrorType('failure');
        });
    }
  }, [allRepOptions, officeOptions]);

  return (
    <Card className='gap-6'>
      <Header className='w-11/12' size="lg">{editingTeam ? 'Edit' : 'Create' } your team</Header>
      <FlashMessage
        message={error}
        type={errorType}
        onClose={() => {
          setError('')
          setErrorType(null)
        }}
      />
      <div className='w-11/12 flex flex-col gap-6 justify-center items-center'>
        <Header className='w-full' size="sm">Team name will be autogenerated for you.</Header>
        { competition.office_restriction && (
            <div style={{width: '100%'}}>
              <AutoSelect
                options={officeOptions}
                value={office}
                setValue={setOffice}
                label={'label'}
                placeholder="Office"
                multi={false}
              />
            </div>
        )}
        { cookies.get('role') === 'admin' && (
            <div style={{width: '100%'}}>
              <AutoSelect
                options={allRepOptions}
                value={owner}
                setValue={handleCreator}
                label={'name'}
                placeholder="Team Owner"
                multi={false}
              />
            </div>
        )}
        { competition.include_address && (
          <div className='w-full labeled'>
            <p className='text-xs text-gray-500 pb-2'>The team address will be used to ensure that all reps are selling within specific area.</p>
            <AddressForm address={address} handleChange={handleChange} />
          </div>
        )}

        <Button
          onClickMethod={handleSubmit}
          disabled={isSubmitting || (editingTeam && !owner)}
          color={(isSubmitting || (editingTeam && !owner)) ? 'gray' : 'yellow'}
        >
          {isSubmitting ? 'Submitting...' : 'Submit'}
        </Button>
      </div>
    </Card>
  );
}

export default TeamBuilder;